/**
 * [TV-2026-05-18] LightweightCandleChart
 *
 * Drop-in replacement for the legacy Canvas chart in TradingPlatform.js using
 * TradingView's official `lightweight-charts` library (Apache 2.0 — free).
 *
 * Props match the legacy CandleChart so swapping is one line:
 *   sym          - {s,l,d,p}                 active symbol meta
 *   prices       - {SYM: lastPrice}         live prices map
 *   mode         - 'opt' | 'cfd'             trading mode (affects colors)
 *   isDark       - bool                      dark theme
 *   tf           - timeframe (string)        not used yet, reserved
 *   openPositions - [{sym,dir,entry,sl,tp,pnl,id,kind}] for P/L lines
 *   indicators   - {ma:bool, ema:bool, bb:bool, rsi:bool, macd:bool}
 *   onChartReady - optional callback(api) when chart mounts
 *
 * Generates 60 mock candles per symbol-change (same as legacy) and live-updates
 * the last candle from `prices[sym.s]` so the chart breathes in real time.
 *
 * P/L lines are drawn via the built-in `createPriceLine` API and live-updated
 * every tick — color flips between green/red based on direction & PnL sign.
 */
import React, { useEffect, useRef, useState } from 'react';
import { createChart, CandlestickSeries, LineSeries } from 'lightweight-charts';

const fmtTitle = (kind, dir, entry, sym) => {
  const lbl = kind === 'opt'
    ? (dir === 'c' || dir === 'call' ? '▲ OPT BUY' : '▼ OPT SELL')
    : (dir === 'b' || dir === 'buy' ? '▲ BUY' : '▼ SELL');
  return `${lbl} @ ${parseFloat(entry).toFixed(sym?.d ?? 5)}`;
};

const LightweightCandleChart = ({
  sym, prices, mode = 'cfd', isDark = false, tf = '1ч',
  openPositions = [], indicators = {},
  marketClosed = false, marketClosedMessage = '',
}) => {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const seriesRef = useRef(null);
  const indicatorSeriesRef = useRef({});  // name -> series
  const positionLinesRef = useRef([]);    // priceLine handles for cleanup
  const [candles, setCandles] = useState([]);
  // [TV-2026-05-19b] Loading state — shows a centered spinner overlay while
  // candles are being fetched; the chart series stays empty so the user
  // never sees a phantom giant candle during the transition.
  const [loading, setLoading] = useState(true);

  // Colors aligned with the trading panel palette
  const palette = isDark
    ? { bg: '#0f0e1a', text: '#e4e6f5', grid: '#1a1a2e', up: '#22c55e', down: '#ef4444',
        border: '#1a1a2e', volume: '#444', sl: '#ef4444', tp: '#3b82f6', entryBuy: '#22c55e', entrySell: '#ef4444' }
    : { bg: '#ffffff', text: '#1a1a2e', grid: '#eef0f5', up: '#22c55e', down: '#ef4444',
        border: '#cfd3df', volume: '#ccc', sl: '#ef4444', tp: '#3b82f6', entryBuy: '#22c55e', entrySell: '#ef4444' };

  // ── Initialize chart once per (theme,symbol) ─────────────────────────────
  useEffect(() => {
    if (!containerRef.current) return;
    const chart = createChart(containerRef.current, {
      width: containerRef.current.clientWidth,
      height: containerRef.current.clientHeight,
      layout: { background: { color: palette.bg }, textColor: palette.text },
      grid: { vertLines: { color: palette.grid }, horzLines: { color: palette.grid } },
      crosshair: { mode: 1 },
      rightPriceScale: { borderColor: palette.border },
      timeScale: { borderColor: palette.border, timeVisible: true, secondsVisible: false },
      autoSize: true,
    });
    const series = chart.addSeries(CandlestickSeries, {
      upColor: palette.up, downColor: palette.down,
      borderUpColor: palette.up, borderDownColor: palette.down,
      wickUpColor: palette.up, wickDownColor: palette.down,
    });
    chartRef.current = chart;
    seriesRef.current = series;

    const onResize = () => {
      if (!containerRef.current || !chartRef.current) return;
      chartRef.current.applyOptions({
        width: containerRef.current.clientWidth,
        height: containerRef.current.clientHeight,
      });
    };
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      chart.remove();
      chartRef.current = null;
      seriesRef.current = null;
      indicatorSeriesRef.current = {};
      positionLinesRef.current = [];
    };
  }, [isDark, sym.s]);

  // ── Seed candles when symbol or timeframe changes ─────────────────────────
  // [CANDLES-2026-05-19] Fetch REAL historical OHLC from Yahoo Finance via
  // `/api/market/candles`. Fallback to the original mock generator only if
  // the API returns empty (e.g. unsupported symbol or network error). This
  // makes technical indicators reflect actual market data.
  const TF_SECONDS = { '1м': 60, '5м': 300, '15м': 900, '1ч': 3600,
                       '4ч': 14400, '1д': 86400, '1н': 604800 };
  useEffect(() => {
    let cancelled = false;
    const tfSec = TF_SECONDS[tf] || 3600;
    const live = prices[sym.s] || sym.p || 1;
    const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

    const buildMockHistory = () => {
      const count = 500;
      const volScale = Math.sqrt(tfSec / 60) * 0.0018;
      const nowSec = Math.floor(Date.now() / 1000);
      const lastBucket = Math.floor(nowSec / tfSec) * tfSec;
      let cursor = live;
      const reverse = [];
      for (let i = 0; i < count; i++) {
        const mv = (Math.random() - 0.5) * cursor * volScale;
        const open = cursor - mv;
        const close = cursor;
        const wickRange = cursor * volScale * 0.55;
        const high = Math.max(open, close) + Math.random() * wickRange;
        const low = Math.min(open, close) - Math.random() * wickRange;
        reverse.push({ time: lastBucket - i * tfSec, open, high, low, close });
        cursor = open;
      }
      return reverse.reverse();
    };

    const applyData = (arr) => {
      if (cancelled) return;
      setCandles(arr);
      if (seriesRef.current) {
        seriesRef.current.setData(arr);
        chartRef.current?.timeScale().fitContent();
      }
      setLoading(false);
    };

    (async () => {
      // [TV-2026-05-19b] Show loading spinner + keep chart empty until data
      // arrives. This guarantees no phantom giant candle is ever visible.
      setLoading(true);
      if (seriesRef.current) {
        try { seriesRef.current.setData([]); } catch (_) {}
      }
      // Also clear indicator overlays so nothing stale flashes
      Object.values(indicatorSeriesRef.current).forEach(s => {
        try { chartRef.current?.removeSeries(s); } catch (_) {}
      });
      indicatorSeriesRef.current = {};
      setCandles([]);
      try {
        const url = `${BACKEND}/api/market/candles?symbol=${encodeURIComponent(sym.s)}&timeframe=${encodeURIComponent(tf)}&limit=500`;
        const r = await fetch(url);
        if (!r.ok) throw new Error('candles fetch failed');
        const json = await r.json();
        const real = Array.isArray(json?.candles) ? json.candles : [];
        if (real.length >= 30) {
          // Ensure ascending time and numeric values
          const cleaned = real
            .map(c => ({
              time: Number(c.time),
              open: Number(c.open),
              high: Number(c.high),
              low: Number(c.low),
              close: Number(c.close),
            }))
            .filter(c => Number.isFinite(c.open) && Number.isFinite(c.close))
            .sort((a, b) => a.time - b.time);
          applyData(cleaned);
          return;
        }
      } catch (_) { /* fall through to mock */ }
      applyData(buildMockHistory());
    })();

    return () => { cancelled = true; };
  }, [sym.s, tf]);  // eslint-disable-line react-hooks/exhaustive-deps

  // ── Live-update last candle from current price ───────────────────────────
  useEffect(() => {
    // [TV-2026-05-19b] Never touch the chart while loading — prevents a live
    // tick from rendering a phantom candle on an otherwise-empty series.
    if (loading) return;
    const live = prices[sym.s];
    if (!live || !seriesRef.current || !candles.length) return;
    const last = candles[candles.length - 1];
    const updated = {
      ...last,
      close: live,
      high: Math.max(last.high, live),
      low: Math.min(last.low, live),
    };
    try { seriesRef.current.update(updated); } catch (_) {}
  }, [prices, sym.s, candles, loading]);

  // ── Indicators (MA/EMA/BB) overlay ───────────────────────────────────────
  useEffect(() => {
    if (!chartRef.current || !candles.length) return;
    const chart = chartRef.current;

    // Remove old indicator series
    Object.values(indicatorSeriesRef.current).forEach(s => {
      try { chart.removeSeries(s); } catch (_) {}
    });
    indicatorSeriesRef.current = {};

    const closes = candles.map(c => c.close);
    const times = candles.map(c => c.time);

    const sma = (n) => closes.map((_, i) => i < n - 1 ? null
      : closes.slice(i - n + 1, i + 1).reduce((a, b) => a + b, 0) / n);
    const ema = (n) => {
      const k = 2 / (n + 1);
      const out = []; let prev = closes[0];
      closes.forEach((c, i) => { prev = i === 0 ? c : c * k + prev * (1 - k); out.push(prev); });
      return out;
    };
    const stdev = (arr, mean) => Math.sqrt(arr.reduce((s, x) => s + (x - mean) ** 2, 0) / arr.length);

    const addLine = (name, values, color, lineWidth = 2) => {
      const s = chart.addSeries(LineSeries, { color, lineWidth, priceLineVisible: false, lastValueVisible: false });
      const data = values.map((v, i) => v == null ? null : { time: times[i], value: v }).filter(Boolean);
      s.setData(data);
      indicatorSeriesRef.current[name] = s;
    };

    if (indicators.ma) addLine('ma', sma(20), '#f59e0b');
    if (indicators.ema) addLine('ema', ema(50), '#a855f7');
    if (indicators.bb) {
      const period = 20;
      const mid = sma(period);
      const upper = closes.map((_, i) => {
        if (i < period - 1) return null;
        const slice = closes.slice(i - period + 1, i + 1);
        return mid[i] + 2 * stdev(slice, mid[i]);
      });
      const lower = closes.map((_, i) => {
        if (i < period - 1) return null;
        const slice = closes.slice(i - period + 1, i + 1);
        return mid[i] - 2 * stdev(slice, mid[i]);
      });
      addLine('bb_mid', mid, '#06b6d4', 1);
      addLine('bb_up', upper, '#06b6d4', 1);
      addLine('bb_lo', lower, '#06b6d4', 1);
    }
    // [TV-2026-05-18] Volume is rendered by a separate SubChart pane in
    // TradingPlatform, so we intentionally do not duplicate it here.
  }, [indicators, candles]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── P/L lines for open positions ─────────────────────────────────────────
  useEffect(() => {
    if (!seriesRef.current) return;
    // Remove old lines
    positionLinesRef.current.forEach(pl => {
      try { seriesRef.current.removePriceLine(pl); } catch (_) {}
    });
    positionLinesRef.current = [];

    const relevant = openPositions.filter(p => (p.sym || p.symbol || p.asset) === sym.s);
    const live = prices[sym.s];
    relevant.forEach(pos => {
      const dir = pos.dir || pos.direction;
      const entry = parseFloat(pos.entry);
      if (!entry) return;
      const isBuy = dir === 'b' || dir === 'buy' || dir === 'c' || dir === 'call';
      // Entry line — color reflects current PnL direction
      const inProfit = live ? (isBuy ? live > entry : live < entry) : true;
      const color = inProfit ? palette.entryBuy : palette.entrySell;
      const pl = seriesRef.current.createPriceLine({
        price: entry,
        color, lineWidth: 2, lineStyle: 0, axisLabelVisible: true,
        title: fmtTitle(pos.kind, dir, entry, sym),
      });
      positionLinesRef.current.push(pl);
      // SL
      if (pos.sl || pos.stop_loss) {
        const slPrice = parseFloat(pos.sl || pos.stop_loss);
        if (slPrice) {
          const sl = seriesRef.current.createPriceLine({
            price: slPrice, color: palette.sl, lineWidth: 1, lineStyle: 2,
            axisLabelVisible: true, title: `SL ${slPrice.toFixed(sym.d || 5)}`,
          });
          positionLinesRef.current.push(sl);
        }
      }
      // TP
      if (pos.tp || pos.take_profit) {
        const tpPrice = parseFloat(pos.tp || pos.take_profit);
        if (tpPrice) {
          const tp = seriesRef.current.createPriceLine({
            price: tpPrice, color: palette.tp, lineWidth: 1, lineStyle: 2,
            axisLabelVisible: true, title: `TP ${tpPrice.toFixed(sym.d || 5)}`,
          });
          positionLinesRef.current.push(tp);
        }
      }
    });
  }, [openPositions, sym.s, prices]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={{ width: '100%', height: '100%', minHeight: 240, position: 'relative' }}>
      <div
        ref={containerRef}
        data-testid="lightweight-chart-container"
        style={{ width: '100%', height: '100%', minHeight: 240 }}
      />
      {loading && (
        <div data-testid="chart-loading-overlay"
          style={{
            position: 'absolute', inset: 0,
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            gap: 14, background: palette.bg, zIndex: 10,
            pointerEvents: 'none',
          }}>
          <div
            style={{
              width: 46, height: 46, borderRadius: '50%',
              border: `3px solid ${isDark ? 'rgba(124, 92, 246, 0.18)' : 'rgba(91, 63, 222, 0.18)'}`,
              borderTopColor: isDark ? '#7c5cf6' : '#5b3fde',
              animation: 'lwchart-spin 0.85s linear infinite',
            }} />
          <div style={{
            fontSize: 12, fontWeight: 600, letterSpacing: 0.4,
            color: isDark ? 'rgba(228, 230, 245, 0.7)' : 'rgba(26, 26, 46, 0.6)',
            textTransform: 'uppercase',
          }}>
            Загрузка графика…
          </div>
          <style>{`@keyframes lwchart-spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}
      {marketClosed && (
        <div data-testid="chart-market-closed-overlay"
          style={{
            position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)',
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '10px 18px', borderRadius: 10,
            background: isDark ? 'rgba(250, 200, 60, 0.16)' : 'rgba(250, 180, 30, 0.95)',
            color: isDark ? '#fbd266' : '#3b2a04',
            border: `1.5px solid ${isDark ? 'rgba(250, 200, 60, 0.45)' : '#d4a017'}`,
            boxShadow: '0 6px 24px rgba(0,0,0,.25)',
            fontSize: 12, fontWeight: 700, letterSpacing: 0.3,
            backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
            maxWidth: '90%', zIndex: 5,
          }}>
          <span style={{ fontSize: 18, lineHeight: 1 }}>⏸</span>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
            <span>Рынок закрыт · {sym.l || sym.s}</span>
            {marketClosedMessage && (
              <span style={{ fontSize: 10, fontWeight: 500, opacity: 0.85 }}>
                {marketClosedMessage}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LightweightCandleChart;
